import React, { useState, useEffect } from 'react';
import { Hausanschluss, LoadProfileType, CONNECTION_KIND_LABELS, connectionKindOf, type ConnectionKind } from '../../../types/network';
import { ConnectionKindBadge } from '../../shared/ConnectionKindBadge';
import { useNetworkElementsStore } from '../../../store/networkElementsStore';
import { useStressTestStore } from '../../../store/stressTestStore';
import { useViewMode } from '../../../context/useViewMode';
import { useNetworkElement } from '../../../hooks/useNetworkElement';
import { useNetworkDataPolling } from '../../../hooks/useNetworkDataPolling';
import { MetersSection } from './meters/MetersSection';
import { PhaseRotationBadge } from './smgw/PhaseRotationBadge';
import { TechnischeRessourceSection } from './TechnischeRessourcenSection';
import { SteuerboxSection } from './SteuerboxSection';
import { PowerMeasurementsSection } from './PowerMeasurementsSection';
import { ElementHistoryModal } from './shared/ElementHistoryModal';
import { ConnectedLineSection } from './connected/ConnectedLineSection';
import { detailStyles, headerColors } from '../../../styles/detailStyles';
import { LoadProfileIdentifier } from '../../../types/loadProfiles';
import { puToVolts, PU_BASE_LN_V, NOMINAL_VOLTAGE_LN_V, puDeviationPct } from '../../../utils/electricalConstants';
import {
  VOLTAGE_BAND_LABEL,
  VOLTAGE_BAND_PCT_LABEL,
  VOLTAGE_OPERATIONAL_BAND_FRACTION,
  VOLTAGE_UNBALANCE_LIMIT_PCT,
  VOLTAGE_WARNING_MARGIN_FRACTION,
} from '../../../utils/voltageConstants';

/** Deviation from Un beyond the EN 50160 band (±10 %) → red. */
const VOLTAGE_DEVIATION_CRIT_PCT = VOLTAGE_OPERATIONAL_BAND_FRACTION * 100;
/** Deviation inside the 3 % warning margin of the band edge (≥ 7 %) → orange. */
const VOLTAGE_DEVIATION_WARN_PCT = (VOLTAGE_OPERATIONAL_BAND_FRACTION - VOLTAGE_WARNING_MARGIN_FRACTION) * 100;
/** Half the EN 50160 unbalance limit → "erhöht" (UI tier, not normative). */
const VOLTAGE_UNBALANCE_ELEVATED_PCT = VOLTAGE_UNBALANCE_LIMIT_PCT / 2;
import { loadProfileService } from '../../../services/loadProfileService';
import { fmt, fmtInt } from '../../../utils/numberFormat';
import { shortElementName } from '../../../utils/elementDisplayName';
import { useReadOnlyUi } from '../../../store/featureStore';

interface HausanschlussDetailsProps {
  element: Hausanschluss;
}

export const HausanschlussDetails: React.FC<HausanschlussDetailsProps> = ({ element: initialElement }) => {
  // Check view mode
  const viewMode = useViewMode();
  const isStressTestMode = viewMode === 'stress-test';
  // Read-only deployments: Stammdaten and Lastprofil are shown, not edited.
  const readOnly = useReadOnlyUi();

  const updateElement = useNetworkElementsStore(state => state.updateElement);
  const element = useNetworkElement<Hausanschluss>(initialElement.id);
  const { markPendingUpdate, clearPendingUpdate } = useNetworkDataPolling(15000);
  const powerMeasurements = useNetworkElementsStore(state => state.powerMeasurements);

  // Stress test data
  const stressTestResults = useStressTestStore(state => state.stressTestResults);
  const stressData = stressTestResults?.results?.[initialElement.id];

  const [historyModalOpen, setHistoryModalOpen] = useState(false);

  // Basic info state
  const [connectionPower, setConnectionPower] = useState(initialElement.connectionPower);
  const [loadProfile, setLoadProfile] = useState<LoadProfileType>(initialElement.loadProfile);
  const [customLoadProfileCode, setCustomLoadProfileCode] = useState<string | undefined>(initialElement.customLoadProfileCode);
  const [annualConsumption, setAnnualConsumption] = useState(initialElement.annualConsumption);
  
  // Update state when element changes from backend
  useEffect(() => {
    if (element) {
      // Only update if the values have changed
      if (element.connectionPower !== connectionPower) {
        setConnectionPower(element.connectionPower);
      }
      if (element.loadProfile !== loadProfile) {
        setLoadProfile(element.loadProfile);
      }
      if (element.customLoadProfileCode !== customLoadProfileCode) {
        setCustomLoadProfileCode(element.customLoadProfileCode);
      }
      if (element.annualConsumption !== annualConsumption) {
        setAnnualConsumption(element.annualConsumption);
      }
    }
  }, [element, connectionPower, loadProfile, customLoadProfileCode, annualConsumption]);

  // Load all profiles on mount
  useEffect(() => {
    const loadProfiles = async () => {
      try {
        const profiles = await loadProfileService.getAllProfiles();
        setAllProfiles(profiles);
      } catch (error) {
        console.error('Failed to load profiles:', error);
      }
    };
    loadProfiles();
  }, []);

  // Update selected profile code when load profile changes
  useEffect(() => {
    setSelectedProfileCode(customLoadProfileCode || loadProfile);
  }, [customLoadProfileCode, loadProfile]);
  
  // UI state
  const [isEditingBasicInfo, setIsEditingBasicInfo] = useState(false);

  // Load profile state
  const [allProfiles, setAllProfiles] = useState<LoadProfileIdentifier[]>([]);
  const [selectedProfileCode, setSelectedProfileCode] = useState<string>(customLoadProfileCode || loadProfile);

  if (!element) return null;

  // Determine voltage values based on mode, preferring SMGW realtime data
  let displayLnVoltageV: number | null;
  let displayLnVoltagePu: number | null;

  if (isStressTestMode) {
    // Stress test mode - show worst (min) phase voltage instead of average
    // voltagePu is the 3-phase average, but the worst phase matters for EN 50160
    const phaseVoltages = [stressData?.voltageL1Pu, stressData?.voltageL2Pu, stressData?.voltageL3Pu]
      .filter((v): v is number => v != null);
    if (phaseVoltages.length > 0) {
      displayLnVoltagePu = Math.min(...phaseVoltages);
      displayLnVoltageV = puToVolts(displayLnVoltagePu);
    } else {
      // Fallback to average if per-phase data not available
      const stressVoltageKv = stressData?.voltageKv ?? null;
      displayLnVoltageV = stressVoltageKv !== null ? (stressVoltageKv * 1000) / Math.sqrt(3) : null;
      displayLnVoltagePu = stressData?.voltagePu ?? null;
    }
  } else {
    // Check SMGW realtime voltages first
    const smgwsWithVoltage = element.smgws?.filter(s => s.hasRealtimeData && s.voltageL1V != null) ?? [];
    if (smgwsWithVoltage.length > 0) {
      // Average SMGW voltages across active gateways, then average across phases
      let vSum = 0, vCount = 0;
      for (const s of smgwsWithVoltage) {
        if (s.voltageL1V != null) { vSum += s.voltageL1V; vCount++; }
        if (s.voltageL2V != null) { vSum += s.voltageL2V; vCount++; }
        if (s.voltageL3V != null) { vSum += s.voltageL3V; vCount++; }
      }
      displayLnVoltageV = vCount > 0 ? vSum / vCount : null;
      displayLnVoltagePu = displayLnVoltageV !== null ? displayLnVoltageV / PU_BASE_LN_V : null;
    } else {
      // Fallback to PandaPower
      const voltageMeasurement = powerMeasurements[element.id];
      const voltageKv = voltageMeasurement?.voltageKV ?? null;
      const voltagePu = voltageMeasurement?.voltagePU ?? null;
      const measuredVoltageV = voltageMeasurement?.measuredVoltageV ?? null;
      const measuredVoltagePu = voltageMeasurement?.measuredVoltagePu ?? null;
      const computedLnVoltageV = voltageKv !== null ? (voltageKv * 1000) / Math.sqrt(3) : null;
      displayLnVoltageV = measuredVoltageV ?? computedLnVoltageV;
      displayLnVoltagePu = measuredVoltagePu ?? voltagePu ?? null;
    }
  }

  const handleConnectionPowerChange = async (value: number) => {
    markPendingUpdate();
    setConnectionPower(value);
    try {
      await updateElement(element.id, { connectionPower: value });
    } finally {
      clearPendingUpdate();
    }
  };

  const handleAnnualConsumptionChange = async (value: number) => {
    markPendingUpdate();
    setAnnualConsumption(value);
    try {
      await updateElement(element.id, { annualConsumption: value });
    } finally {
      clearPendingUpdate();
    }
  };

  const handleLoadProfileChange = async (value: LoadProfileType) => {
    markPendingUpdate();
    setLoadProfile(value);
    try {
      await updateElement(element.id, { loadProfile: value });
    } finally {
      clearPendingUpdate();
    }
  };

  const handleConnectionKindChange = async (value: ConnectionKind) => {
    markPendingUpdate();
    try {
      await updateElement(element.id, { connectionKind: value });
    } finally {
      clearPendingUpdate();
    }
  };

  const handleCustomLoadProfileChange = async (value: string | undefined) => {
    markPendingUpdate();
    setCustomLoadProfileCode(value);
    try {
      await updateElement(element.id, { customLoadProfileCode: value });
    } finally {
      clearPendingUpdate();
    }
  };

  const handleProfileChange = (value: string) => {
    setSelectedProfileCode(value);

    // Check if it's a custom profile
    if (value.startsWith('CUSTOM_')) {
      handleCustomLoadProfileChange(value);
    } else {
      // It's a standard profile
      handleLoadProfileChange(value as LoadProfileType);
      handleCustomLoadProfileChange(undefined);
    }
  };


  return (
    <div className={`flex flex-col ${detailStyles.layout.section}`}>
      {/* Header with Delete Button */}
      <div className={`${detailStyles.header.wrapper} ${headerColors.hausanschluss.bg}`}>
        <div className={detailStyles.header.content}>
          <div className="flex items-center space-x-3">
            <div className={detailStyles.header.iconBox}>
              <svg className={`${detailStyles.header.icon} ${headerColors.hausanschluss.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <div>
              <h2 className={`${detailStyles.header.title} flex items-center gap-2`}>
                Hausanschluss
                <ConnectionKindBadge kind={connectionKindOf(element)} className="normal-case tracking-normal" />
              </h2>
              <p className={detailStyles.header.subtitle}>{initialElement.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Switch Button for Hausanschluss */}
            <button
              onClick={async () => {
                markPendingUpdate();
                try {
                  await updateElement(element.id, { inService: !element.inService });
                } finally {
                  clearPendingUpdate();
                }
              }}
              className={`p-1.5 rounded transition-all ${
                element.inService !== false
                  ? 'bg-green-500 hover:bg-green-600 text-white'
                  : 'bg-red-500 hover:bg-red-600 text-white'
              }`}
              title={element.inService !== false ? 'Hausanschluss ausschalten' : 'Hausanschluss einschalten'}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </button>
          </div>
        </div>
        <div className={`${detailStyles.header.id} ${headerColors.hausanschluss.id}`}>ID: {initialElement.id}</div>
      </div>

      {/* Stammdaten Card */}
      <div className="relative bg-white dark:bg-primary-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-primary-600">
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-400 dark:bg-green-500" />
        <div className="pl-4 pr-3 py-3">
          {isStressTestMode && stressData ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 dark:bg-primary-700/50 rounded-md px-3 py-2">
                  <div className={`text-lg font-bold ${stressData.isExporting ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}>
                    {stressData.powerKw !== undefined ? `${fmt(Math.abs(stressData.powerKw), 1)}` : 'N/A'} <span className="text-xs font-normal text-gray-500 dark:text-gray-400">kW</span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Netto-Leistung</div>
                  <div className={`text-[10px] mt-0.5 ${stressData.isExporting ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}>
                    {stressData.isExporting ? '\u2191 Einspeisung' : '\u2193 Verbrauch'}
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-primary-700/50 rounded-md px-3 py-2">
                  <div className="text-lg font-bold text-gray-900 dark:text-white">
                    {stressData.baseLoadKw !== undefined ? `${fmt(stressData.baseLoadKw, 1)}` : 'N/A'} <span className="text-xs font-normal text-gray-500 dark:text-gray-400">kW</span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Grundlast</div>
                  {stressData.hasPv && stressData.pvGenerationKw !== undefined && (
                    <div className="text-[10px] mt-0.5 text-yellow-600 dark:text-yellow-400">
                      PV: {fmt(stressData.pvGenerationKw, 1)} kW
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="shrink-0 w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                    <svg className="w-3.5 h-3.5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Stammdaten</span>
                </div>
                {!readOnly && (
                <button
                  onClick={() => setIsEditingBasicInfo(!isEditingBasicInfo)}
                  className="text-gray-400 dark:text-primary-500 hover:text-gray-600 dark:hover:text-primary-300"
                  title={isEditingBasicInfo ? "Fertig" : "Bearbeiten"}
                >
                  {isEditingBasicInfo ? (
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  )}
                </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-gray-50 dark:bg-primary-700/50 rounded-md px-3 py-2">
                  {isEditingBasicInfo ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        value={annualConsumption}
                        onChange={(e) => handleAnnualConsumptionChange(Number(e.target.value))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-700 text-gray-900 dark:text-primary-100"
                      />
                      <span className="text-xs text-gray-500 dark:text-primary-400">kWh</span>
                    </div>
                  ) : (
                    <>
                      <div className="text-lg font-bold text-gray-900 dark:text-white">
                        {fmtInt(annualConsumption)} <span className="text-xs font-normal text-gray-500 dark:text-gray-400">kWh</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Jahresverbrauch</div>
                    </>
                  )}
                </div>
                <div className="bg-gray-50 dark:bg-primary-700/50 rounded-md px-3 py-2">
                  {isEditingBasicInfo ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        value={connectionPower}
                        onChange={(e) => handleConnectionPowerChange(Number(e.target.value))}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-700 text-gray-900 dark:text-primary-100"
                      />
                      <span className="text-xs text-gray-500 dark:text-primary-400">kW</span>
                    </div>
                  ) : (
                    <>
                      <div className="text-lg font-bold text-gray-900 dark:text-white">
                        {fmt(connectionPower, 1)} <span className="text-xs font-normal text-gray-500 dark:text-gray-400">kW</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Anschlussleistung</div>
                    </>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Anschlussart — what sits behind the connection (doc/Architektur/ANSCHLUSSART.md) */}
          {!isStressTestMode && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">Anschlussart</span>
              {readOnly ? (
                <span className="flex-1 min-w-0 truncate text-xs text-gray-900 dark:text-primary-100">
                  {CONNECTION_KIND_LABELS[connectionKindOf(element)]}
                </span>
              ) : (
                <select
                  value={connectionKindOf(element)}
                  onChange={(e) => handleConnectionKindChange(e.target.value as ConnectionKind)}
                  className="flex-1 min-w-0 px-2 py-1 text-xs border border-gray-200 dark:border-primary-500 rounded bg-white dark:bg-primary-600 text-gray-900 dark:text-primary-100 focus:outline-hidden focus:ring-1 focus:ring-green-500"
                  title="RLM-Kunden und Anschlusssäulen rechnen mit ihrer Anschlussleistung, Erzeugungsanlagen ohne Last"
                >
                  {(Object.keys(CONNECTION_KIND_LABELS) as ConnectionKind[]).map(k => (
                    <option key={k} value={k}>{CONNECTION_KIND_LABELS[k]}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Lastprofil - integrated into card */}
          {!isStressTestMode && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">Lastprofil</span>
              {selectedProfileCode.startsWith('CUSTOM_') && (
                <span className="text-[10px] bg-info-500/20 text-info-700 dark:text-info-400 px-1.5 py-0.5 rounded shrink-0">
                  Benutzerdefiniert
                </span>
              )}
              {readOnly ? (
                <span className="flex-1 min-w-0 truncate text-xs text-gray-900 dark:text-primary-100">
                  {allProfiles.find(p => p.code === selectedProfileCode)?.description
                    ? `${selectedProfileCode} – ${allProfiles.find(p => p.code === selectedProfileCode)?.description}`
                    : (allProfiles.find(p => p.code === selectedProfileCode)?.name ?? selectedProfileCode)}
                </span>
              ) : (
              <select
                value={selectedProfileCode}
                onChange={(e) => handleProfileChange(e.target.value)}
                className="flex-1 min-w-0 px-2 py-1 text-xs border border-gray-200 dark:border-primary-500 rounded bg-white dark:bg-primary-600 text-gray-900 dark:text-primary-100 focus:outline-hidden focus:ring-1 focus:ring-green-500"
              >
                <optgroup label="Standardprofile">
                  {allProfiles.filter(p => !p.isCustom).map(profile => (
                    <option key={profile.code} value={profile.code}>
                      {profile.code} - {profile.description}
                    </option>
                  ))}
                </optgroup>
                {allProfiles.some(p => p.isCustom) && (
                  <optgroup label="Benutzerdefinierte Profile">
                    {allProfiles.filter(p => p.isCustom).map(profile => (
                      <option key={profile.code} value={profile.code}>
                        {profile.name}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Power Measurements Section */}
      <PowerMeasurementsSection
        elementId={element.id}
        smgws={element.smgws}
        connectionPowerKw={element.connectionPower}
      />

      {/* Historical loadflow series from the snapshot archive (incl. voltage band) */}
      <button
        onClick={() => setHistoryModalOpen(true)}
        className="w-full px-3 py-2 text-sm rounded-md border border-gray-200 dark:border-primary-600 bg-white dark:bg-primary-800 text-gray-700 dark:text-primary-200 hover:bg-gray-50 dark:hover:bg-primary-700 transition-colors flex items-center justify-center gap-2 cursor-pointer"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        Lastfluss-Historie (Spannung &amp; Leistung)
      </button>

      <ElementHistoryModal
        isOpen={historyModalOpen}
        elementId={element.id}
        elementName={shortElementName(element) ?? element.name}
        showVoltage
        onClose={() => setHistoryModalOpen(false)}
      />

      {/* Zähler: every Messlokation / SMGW meter once, value + source, one view per TAF profile */}
      <MetersSection
        element={element}
        onUpdateSmgws={async (smgws) => {
          markPendingUpdate();
          try {
            await updateElement(element.id, { smgws });
          } finally {
            clearPendingUpdate();
          }
        }}
      />


      

      {/* Steuerbox Section */}
      <SteuerboxSection
        steuerboxes={element.steuerboxes}
        technischeRessourcen={element.technischeRessourcen}
        smgwSerials={(element.smgws ?? []).map(s => s.serialNumber).filter(Boolean)}
        onUpdate={async (steuerboxes) => {
          markPendingUpdate();
          try {
            await updateElement(element.id, { steuerboxes });
          } finally {
            clearPendingUpdate();
          }
        }}
      />

      {/* Technische Ressourcen Section */}
      <TechnischeRessourceSection
        technischeRessourcen={element.technischeRessourcen}
        elementId={element.id}
        onUpdate={async (technischeRessourcen) => {
          markPendingUpdate();
          try {
            await updateElement(element.id, { technischeRessourcen });
          } finally {
            clearPendingUpdate();
          }
        }}
      />



      {/* Voltage Section */}
      <div className="relative bg-white dark:bg-primary-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-primary-600">
        <div className={`absolute left-0 top-0 bottom-0 w-1 ${isStressTestMode ? 'bg-orange-400 dark:bg-orange-500' : 'bg-blue-400 dark:bg-blue-500'}`} />
        <div className="pl-4 pr-3 py-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                isStressTestMode ? 'bg-orange-100 dark:bg-orange-900/40' : 'bg-blue-100 dark:bg-blue-900/40'
              }`}>
                <svg className={`w-3.5 h-3.5 ${isStressTestMode ? 'text-orange-600 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Spannung</span>
            </div>
            {isStressTestMode && (
              <span className="text-[10px] bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 px-1.5 py-0.5 rounded font-medium">
                Stresstest
              </span>
            )}
          </div>

          {/* Main voltage metrics - value first */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="bg-gray-50 dark:bg-primary-700/50 rounded-md px-3 py-2">
              <div className={`text-lg font-bold ${
                isStressTestMode ? 'text-orange-600 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400'
              }`}>
                {displayLnVoltageV !== null ? fmt(displayLnVoltageV, 1) : '\u2013'} <span className="text-xs font-normal text-gray-500 dark:text-gray-400">V</span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{isStressTestMode ? 'Min Spannung L-N' : 'Spannung L-N'}</div>
            </div>
            <div className="bg-gray-50 dark:bg-primary-700/50 rounded-md px-3 py-2">
              <div className={`text-lg font-bold ${
                displayLnVoltagePu !== null && Math.abs(puDeviationPct(displayLnVoltagePu)) > VOLTAGE_DEVIATION_CRIT_PCT
                  ? 'text-red-600 dark:text-red-400'
                  : displayLnVoltagePu !== null && Math.abs(puDeviationPct(displayLnVoltagePu)) > VOLTAGE_DEVIATION_WARN_PCT
                    ? 'text-orange-600 dark:text-orange-400'
                    : 'text-green-600 dark:text-green-400'
              }`}>
                {displayLnVoltagePu !== null ? `${puDeviationPct(displayLnVoltagePu) > 0 ? '+' : ''}${fmt(puDeviationPct(displayLnVoltagePu), 1)}` : '\u2013'} <span className="text-xs font-normal text-gray-500 dark:text-gray-400">%</span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400" title={`Bezug: Nennspannung Un = ${NOMINAL_VOLTAGE_LN_V} V (DIN EN 50160); zulässiges Band ${VOLTAGE_BAND_LABEL} (${VOLTAGE_BAND_PCT_LABEL})`}>Abweichung von {NOMINAL_VOLTAGE_LN_V} V (Un)</div>
            </div>
          </div>

          {/* Three-Phase Voltages - compact */}
          {(() => {
            const voltageMeasurement = powerMeasurements[element.id];
            const voltageUnbalancePercent = voltageMeasurement?.voltageUnbalancePercent;

            const activeSmgws = element.smgws?.filter(s => s.hasRealtimeData && s.voltageL1V != null) ?? [];
            const hasSmgwVoltages = activeSmgws.length > 0 && !isStressTestMode;
            // Phase rotation: only show the badge when the displayed values
            // actually come from a rotated SMGW. We don't surface it on the
            // PandaPower fallback because the rotation is per-meter and a
            // PF result can't carry that provenance.
            const rotatedSourceSmgw = hasSmgwVoltages
              ? activeSmgws.find(s => s.phaseRotation && s.phaseRotation !== 0)
              : undefined;

            let voltageL1V: number | null;
            let voltageL2V: number | null;
            let voltageL3V: number | null;
            let voltageL1Pu: number | null;
            let voltageL2Pu: number | null;
            let voltageL3Pu: number | null;
            let phaseVoltageSource: string;

            const nominalV = PU_BASE_LN_V;

            if (hasSmgwVoltages) {
              let vL1Sum = 0, vL2Sum = 0, vL3Sum = 0;
              let vL1Count = 0, vL2Count = 0, vL3Count = 0;
              for (const s of activeSmgws) {
                if (s.voltageL1V != null) { vL1Sum += s.voltageL1V; vL1Count++; }
                if (s.voltageL2V != null) { vL2Sum += s.voltageL2V; vL2Count++; }
                if (s.voltageL3V != null) { vL3Sum += s.voltageL3V; vL3Count++; }
              }
              voltageL1V = vL1Count > 0 ? vL1Sum / vL1Count : null;
              voltageL2V = vL2Count > 0 ? vL2Sum / vL2Count : null;
              voltageL3V = vL3Count > 0 ? vL3Sum / vL3Count : null;
              voltageL1Pu = voltageL1V !== null ? voltageL1V / nominalV : null;
              voltageL2Pu = voltageL2V !== null ? voltageL2V / nominalV : null;
              voltageL3Pu = voltageL3V !== null ? voltageL3V / nominalV : null;
              phaseVoltageSource = 'smgw';
            } else {
              const ppL1Pu = voltageMeasurement?.voltageL1Pu;
              const ppL2Pu = voltageMeasurement?.voltageL2Pu;
              const ppL3Pu = voltageMeasurement?.voltageL3Pu;
              voltageL1Pu = ppL1Pu ?? null;
              voltageL2Pu = ppL2Pu ?? null;
              voltageL3Pu = ppL3Pu ?? null;
              voltageL1V = voltageL1Pu !== null ? voltageL1Pu * nominalV : null;
              voltageL2V = voltageL2Pu !== null ? voltageL2Pu * nominalV : null;
              voltageL3V = voltageL3Pu !== null ? voltageL3Pu * nominalV : null;
              phaseVoltageSource = 'pandapower';
            }

            const hasThreePhaseData = voltageL1V !== null;

            if (!hasThreePhaseData && !isStressTestMode) {
              return null;
            }

            const getVoltageColor = (pu: number | null | undefined) => {
              if (pu === null || pu === undefined) return 'text-gray-500 dark:text-gray-400';
              const deviation = Math.abs(puDeviationPct(pu));
              if (deviation > VOLTAGE_DEVIATION_CRIT_PCT) return 'text-red-600 dark:text-red-400';
              if (deviation > VOLTAGE_DEVIATION_WARN_PCT) return 'text-orange-600 dark:text-orange-400';
              return 'text-green-600 dark:text-green-400';
            };

            return (
              <>
                {rotatedSourceSmgw && (
                  <div className="mb-2">
                    <PhaseRotationBadge rotation={rotatedSourceSmgw.phaseRotation} size="pill" />
                  </div>
                )}
                {/* Compact 3-column phase row */}
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {([
                    { label: 'L1', v: voltageL1V, pu: voltageL1Pu },
                    { label: 'L2', v: voltageL2V, pu: voltageL2Pu },
                    { label: 'L3', v: voltageL3V, pu: voltageL3Pu },
                  ] as const).map(({ label, v, pu }) => (
                    <div key={label} className="text-center bg-gray-50 dark:bg-primary-700/50 rounded-md py-1.5 px-1">
                      <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500">{label}</p>
                      <p className={`text-sm font-semibold ${getVoltageColor(pu)}`}>
                        {v !== null ? `${fmt(v, 1)} V` : '\u2013'}
                      </p>
                      <p className={`text-[10px] ${getVoltageColor(pu)}`}>
                        {pu !== null ? `${fmt(puDeviationPct(pu), 1)}%` : '\u2013'}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Unsymmetrie + source as compact footer. A calculated
                    unbalance of ~0 with three (near-)identical phase voltages
                    means the model distributed the loads symmetrically — that
                    is an assumption, not a measurement, and is labelled so. */}
                <div className="flex items-center justify-between text-[10px] text-gray-500 dark:text-gray-400">
                  {voltageUnbalancePercent !== null && voltageUnbalancePercent !== undefined ? (
                    phaseVoltageSource !== 'smgw' && voltageUnbalancePercent < 0.005 ? (
                      <span title="Der Lastfluss hat die Lasten dieses Anschlusses symmetrisch auf L1/L2/L3 verteilt (keine phasenscharfe Messung) — die berechnete Unsymmetrie ist deshalb konstruktionsbedingt ≈ 0 und keine Messaussage.">
                        Unsymmetrie: <span className="font-semibold">≈ 0 %</span> (symmetrische Lastannahme)
                      </span>
                    ) : (
                    <span>
                      Unsymmetrie:{' '}
                      <span className={`font-semibold ${
                        voltageUnbalancePercent > VOLTAGE_UNBALANCE_LIMIT_PCT ? 'text-red-600 dark:text-red-400' :
                        voltageUnbalancePercent > VOLTAGE_UNBALANCE_ELEVATED_PCT ? 'text-orange-600 dark:text-orange-400' :
                        'text-green-600 dark:text-green-400'
                      }`}>
                        {fmt(voltageUnbalancePercent, 2)} %
                      </span>
                      {' '}
                      ({voltageUnbalancePercent > VOLTAGE_UNBALANCE_LIMIT_PCT ? 'kritisch' : voltageUnbalancePercent > VOLTAGE_UNBALANCE_ELEVATED_PCT ? 'erhöht' : 'normal'})
                    </span>
                    )
                  ) : <span />}
                  <span title={phaseVoltageSource === 'smgw' ? 'Phasenspannungen vom Smart Meter Gateway gemessen' : 'Phasenspannungen aus der Lastflussberechnung'}>
                    Quelle: {phaseVoltageSource === 'smgw' ? 'SMGW-Messung' : 'Lastfluss'}
                  </span>
                </div>
              </>
            );
          })()}
        </div>
      </div>
      {/* Connected Line Information */}
      <ConnectedLineSection hausanschluss={element} />

    </div>
  );
};
